import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGoToPortal } from '../../hooks/useGoToPortal';
import { canAccessAdminHub } from '../../lib/adminHubPermissions';
import type { PortalKind } from '../../lib/portal';
import type { PortalAccessMap } from '../../lib/portalAccess';

type PortalOption = {
  kind: PortalKind;
  label: string;
  hasAccess: boolean;
};

const PORTAL_OPTIONS: { kind: PortalKind; label: string; shortLabel: string }[] = [
  { kind: 'crm', label: 'Customer Relationship Management', shortLabel: 'CRM' },
  { kind: 'it', label: 'IT Management', shortLabel: 'IT' },
  { kind: 'dem', label: 'Data Entry Management', shortLabel: 'DEM' },
  { kind: 'forms', label: 'Application Form', shortLabel: 'Forms' },
  { kind: 'accounts', label: 'Accounts', shortLabel: 'Accounts' },
  { kind: 'report', label: 'Report', shortLabel: 'Report' },
  { kind: 'purchasing', label: 'Purchasing', shortLabel: 'Purchasing' },
  { kind: 'design', label: 'Design', shortLabel: 'Design' },
];

function sideHasAccess(map: PortalAccessMap | null | undefined, kind: PortalKind): boolean {
  if (!map) return false;
  if (kind === 'crm') return map.crm.hasAccess;
  if (kind === 'it') return map.it.hasAccess;
  if (kind === 'dem') return map.dem.hasAccess;
  if (kind === 'forms') return map.forms.hasAccess;
  if (kind === 'accounts') return map.accounts.hasAccess;
  if (kind === 'report') return map.report.hasAccess;
  if (kind === 'purchasing') return map.purchasing.hasAccess;
  if (kind === 'design') return map.design.hasAccess;
  return false;
}

type PortalSwitcherProps = {
  current: PortalKind;
  /** Override the trigger label (e.g. "Choose portal"). */
  triggerLabel?: string;
  /** Hide the ⊞ icon in the trigger. */
  hideIcon?: boolean;
};

export function PortalSwitcher({ current, triggerLabel, hideIcon = false }: PortalSwitcherProps) {
  const { portalAccess } = useAuth();
  const navigate = useNavigate();
  const goToPortal = useGoToPortal();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const options: PortalOption[] = PORTAL_OPTIONS.map((option) => ({
    ...option,
    hasAccess: sideHasAccess(portalAccess, option.kind),
  }));

  const accessibleCount = options.filter((option) => option.hasAccess).length;
  const showSettings = canAccessAdminHub(portalAccess);
  const onSettingsPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const currentLabel = onSettingsPage
    ? 'Settings'
    : (options.find((option) => option.kind === current)?.label ?? 'Apps');
  const currentShortLabel = onSettingsPage
    ? 'Settings'
    : (PORTAL_OPTIONS.find((option) => option.kind === current)?.shortLabel ?? 'Apps');
  const buttonLabel = triggerLabel ?? currentShortLabel;

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!portalAccess || (accessibleCount <= 1 && !showSettings)) {
    return null;
  }

  return (
    <div className="portal-switcher" ref={rootRef}>
      <button
        type="button"
        className="btn-secondary btn-sm portal-switcher__trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Switch workspace (${currentLabel})`}
        onClick={() => setOpen((value) => !value)}
      >
        {!hideIcon ? (
          <span className="portal-switcher__icon" aria-hidden>
            ⊞
          </span>
        ) : null}
        <span className="portal-switcher__label">{buttonLabel}</span>
        <span className="portal-switcher__chevron" aria-hidden>
          ▾
        </span>
      </button>

      {open ? (
        <div className="portal-switcher__menu" role="menu">
          {options
            .filter((option) => option.hasAccess)
            .map((option) => {
            const isCurrent = !onSettingsPage && option.kind === current;
            return (
              <button
                key={option.kind}
                type="button"
                role="menuitem"
                className={`portal-switcher__item${isCurrent ? ' portal-switcher__item--current' : ''}`}
                disabled={isCurrent}
                onClick={() => {
                  if (isCurrent) return;
                  setOpen(false);
                  goToPortal(option.kind);
                }}
              >
                <span className="portal-switcher__item-title">{option.label}</span>
                {isCurrent ? <span className="portal-switcher__item-hint">Current</span> : null}
              </button>
            );
          })}

          {showSettings ? (
            <>
              {accessibleCount > 0 ? <div className="portal-switcher__divider" role="separator" /> : null}
              <button
                type="button"
                role="menuitem"
                className={`portal-switcher__item${onSettingsPage ? ' portal-switcher__item--current' : ''}`}
                disabled={onSettingsPage}
                onClick={() => {
                  if (onSettingsPage) return;
                  setOpen(false);
                  navigate('/admin');
                }}
              >
                <span className="portal-switcher__item-title">Settings</span>
                {onSettingsPage ? (
                  <span className="portal-switcher__item-hint">Current</span>
                ) : (
                  <span className="portal-switcher__item-hint">All systems</span>
                )}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
